import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import {
  nsColorConstants,
  dateTimeFormat
} from '../../../../util/GlobalConstant';

import {
  ResponsiveContainer,
  AreaChart,
  XAxis,
  YAxis,
  Area,
  CartesianGrid,
  Tooltip,
  Label,
  Legend
} from 'recharts';
import MinimizedPanel from '../../../../components/MinimizedPanel';
import NoDataIsLoadingWrapper from '../../../../components/NoDataIsLoadingWrapper';
import { flattenChartData } from './chartsUtil';
import style from './index.scss';

export const HitsDistributionChart = (props) => {
  const {
    data,
    isLoading,
    isCollapsed,
    onTogglePanelVisibility,
    onReload
  } = props;

  if (isCollapsed) {
    return (
      <MinimizedPanel
        onTogglePanelVisibility={onTogglePanelVisibility}
        title="Hits Distribution"
        isCollapsed={isCollapsed}
      />
    );
  }

  if (isLoading) {
    return (
      <NoDataIsLoadingWrapper
        onTogglePanelVisibility={onTogglePanelVisibility}
        title="Hits Distribution"
        isLoadingDataText="Loading Hits Distribution chart data..."
        isCollapsed={isCollapsed}
      />
    );
  }

  if (Array.isArray(data) && data.length > 0) {
    const flattenedData = flattenChartData(data, [
      'level1',
      'undetermined',
      'trueHits',
      'falseHits'
    ]);
    return (
      <div className="card">
        <MinimizedPanel
          onTogglePanelVisibility={onTogglePanelVisibility}
          title="Hits Distribution"
          isCollapsed={isCollapsed}
        />
        <div className="card-body">
          <div className="row">
            <div className="col-md-12">
              {/* Figure Area Chart goes here */}
              <ResponsiveContainer
                width="100%"
                height={260}
                margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
                <AreaChart data={flattenedData}>
                  <defs>
                    <linearGradient
                      id="colorTrueHits"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop
                        offset="5%"
                        stopColor={nsColorConstants.trueHits}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="95%"
                        stopColor={nsColorConstants.trueHits}
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient
                      id="colorFalseHits"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop
                        offset="5%"
                        stopColor={nsColorConstants.falseHits}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="95%"
                        stopColor={nsColorConstants.falseHits}
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient
                      id="colorUndetermined"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop
                        offset="5%"
                        stopColor={nsColorConstants.undetermined}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="95%"
                        stopColor={nsColorConstants.undetermined}
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient
                      id="colorAutoClosed"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop
                        offset="5%"
                        stopColor={nsColorConstants.autoClosed}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="95%"
                        stopColor={nsColorConstants.autoClosed}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <XAxis
                    height={60}
                    tickCount={5}
                    dataKey="date"
                    tickFormatter={dateTickFormatter}
                    tick={<CustomizedAxisTick />}
                  />
                  <YAxis>
                    <Label
                      value="Count"
                      position="insideLeft"
                      angle={-90}
                      style={{ textAnchor: 'middle' }}
                    />
                  </YAxis>

                  <CartesianGrid strokeDasharray="5 5" vertical={false} />
                  <Tooltip content={<CustomTooltip data={flattenedData} />} />
                  <Legend formatter={legendFormatter} />
                  <Area
                    type="monotone"
                    dataKey="level1"
                    stroke={nsColorConstants.autoClosed}
                    fillOpacity={0.8}
                    fill="url(#colorAutoClosed)"
                  />
                  <Area
                    type="monotone"
                    dataKey="undetermined"
                    stroke={nsColorConstants.undetermined}
                    fillOpacity={0.8}
                    fill="url(#colorUndetermined)"
                  />
                  <Area
                    type="monotone"
                    dataKey="trueHits"
                    stroke={nsColorConstants.falseHits}
                    fillOpacity={0.8}
                    fill="url(#colorFalseHits)"
                  />
                  <Area
                    type="monotone"
                    dataKey="falseHits"
                    stroke={nsColorConstants.trueHits}
                    fillOpacity={0.8}
                    fill="url(#colorTrueHits)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <NoDataIsLoadingWrapper
      onReload={onReload}
      onTogglePanelVisibility={onTogglePanelVisibility}
      title="Hits Distribution"
      noDataText="No Hits Distribution data exists for the period selected."
      reloadDataText="Click here to fetch hits distribution data again."
    />
  );
};

const dateTickFormatter = (tickitem) => {
  const tickDate = moment(tickitem, [dateTimeFormat]);
  if (tickDate.isValid()) {
    return moment(tickitem, [dateTimeFormat]).format('DD/MM/YYYY');
  }

  return '';
};

const CustomTooltip = (props) => {
  const { active, payload, text, label, data } = props;

  if (!data || !payload) {
    return null;
  }

  let pointLists = [];
  payload.forEach((item, i) => {
    let itemNameLabel = item.name;
    if (item.name === 'falseHits') {
      itemNameLabel = 'False Hits';
    }

    if (item.name === 'level1') {
      itemNameLabel = 'L1 Hits';
    }

    if (item.name === 'trueHits') {
      itemNameLabel = 'True Hits';
    }

    if (item.name === 'undetermined') {
      itemNameLabel = 'Undetermined Hits';
    }
    pointLists.push(
      <li key={i} style={{ color: `${item.stroke}` }}>
        {itemNameLabel} : <span style={{ float: 'right' }}>{item.value}</span>
      </li>
    );
  });

  let accumulatedLists = [];
  payload.forEach((item, i) => {
    let itemNameLabel = item.name;
    if (item.name === 'falseHits') {
      itemNameLabel = 'False Hits';
    }

    if (item.name === 'level1') {
      itemNameLabel = 'L1 Hits';
    }

    if (item.name === 'trueHits') {
      itemNameLabel = 'True Hits';
    }

    if (item.name === 'undetermined') {
      itemNameLabel = 'Undetermined Hits';
    }
    accumulatedLists.push(
      <li key={i} style={{ color: `${item.stroke}` }}>
        {itemNameLabel} :{' '}
        <span style={{ float: 'right' }}>
          {accumulater(data, label, item.name)}
        </span>
      </li>
    );
  });

  let startingDate = '';
  let selectedDate = '';

  const startingDateValue = moment.utc(data[0].date, [dateTimeFormat]);
  if (startingDateValue.isValid()) {
    startingDate = startingDateValue.local().format('DD/MM/YYYY');
  }

  const selectedDateValue = moment.utc(label, [dateTimeFormat]);
  if (selectedDateValue.isValid()) {
    selectedDate = selectedDateValue.local().format('DD/MM/YYYY');
  }

  return active ? (
    <div className={style.customTooltip}>
      <div className="row">
        <div className="col-sm-12">
          <p>
            <i className="fa fa-calendar" /> {selectedDate}
          </p>
          <ul className="list-unstyled">{pointLists}</ul>
        </div>

        <svg height="10" width="180">
          <line
            x1="20"
            y1="2"
            x2="180"
            y2="2"
            style={{ stroke: 'rgb(133,133,0)', strokeWidth: 1 }}
          />
        </svg>

        <div className="col-sm-12">
          <p>
            <i className="fa fa-calendar" /> {startingDate} -{'>'}{' '}
            {selectedDate}
          </p>
          <ul className="list-unstyled">{accumulatedLists}</ul>
        </div>
      </div>
    </div>
  ) : null;
};

const accumulater = (data, label, name) => {
  let result = 0;

  let i = 0;
  let isLooping = true;
  while (isLooping) {
    const item = data[i];
    result += item[name];

    i++;

    if (item.date === label) {
      isLooping = false;
    }
  }

  return result;
};

const legendFormatter = (value, entry, index) => {
  if (value) {
    if (value === 'falseHits') {
      return 'False Hits';
    }

    if (value === 'level1') {
      return 'L1 Hits';
    }

    if (value === 'trueHits') {
      return 'True Hits';
    }

    if (value === 'undetermined') {
      return 'Undetermined Hits';
    }
  }

  return '';
};

const CustomizedAxisTick = (props) => {
  const { x, y, stroke, payload, visibleTicksCount, index } = props;
  const tickValue = moment.utc(payload.value, [dateTimeFormat]);

  if (tickValue.isValid()) {
    const formattedTickDate = tickValue.local().format('DD/MM/YYYY');
    if (index === visibleTicksCount - 1) {
      return (
        <g transform={`translate(${x + 20},${y})`}>
          <text
            x={0}
            y={0}
            dy={12}
            textAnchor="end"
            fill="#666"
            transform="rotate(-35)">
            {formattedTickDate}
          </text>
        </g>
      );
    } else {
      return (
        <g transform={`translate(${x},${y})`}>
          <text
            x={0}
            y={0}
            dy={12}
            textAnchor="end"
            fill="#666"
            transform="rotate(-35)">
            {formattedTickDate}
          </text>
        </g>
      );
    }
  }

  if (index === visibleTicksCount - 1) {
    return (
      <g transform={`translate(${x + 20},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="end"
          fill="#666"
          transform="rotate(-35)">
          {payload.value}
        </text>
      </g>
    );
  } else {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="end"
          fill="#666"
          transform="rotate(-35)">
          {payload.value}
        </text>
      </g>
    );
  }
};

HitsDistributionChart.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onTogglePanelVisibility: PropTypes.func,
  onReload: PropTypes.func
};
